import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Award, CheckCircle2, Calendar, Building2, ShieldCheck, AlertCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { FloatingWhatsAppButton } from "@/components/FloatingWhatsAppButton";
import { generateDiplomaImage, sanitizeDiplomaText } from "@/lib/generateDiplomaPDF";
import { normalizeDiplomaLayout } from "@/hooks/useCertificateConfig";

interface VerifyData {
  id: string;
  user_name: string;
  certificate_name: string;
  generated_at: string;
  diploma_type: 'A' | 'B';
  og_image_url: string | null;
  program_certificate: any | null;
}

export default function CertificateVerifyPage() {
  const { certLogId } = useParams<{ certLogId: string }>();
  const [data, setData] = useState<VerifyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [renderedImageUrl, setRenderedImageUrl] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    async function load() {
      if (!certLogId) return;
      setLoading(true);
      const { data: rows, error } = await (supabase as any)
        .rpc('verify_certificate', { _cert_id: certLogId });
      const log = Array.isArray(rows) ? rows[0] : rows;
      if (canceled) return;
      if (error || !log) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      let pc: any = null;
      if (log.program_certificate_id) {
        const { data: cert } = await supabase
          .from('program_certificates')
          .select('*')
          .eq('id', log.program_certificate_id)
          .maybeSingle();
        pc = cert;
      }
      setData({
        id: log.id,
        user_name: sanitizeDiplomaText(log.user_name ?? ""),
        certificate_name: log.certificate_name,
        generated_at: log.generated_at,
        diploma_type: (log.diploma_type ?? 'A') as 'A' | 'B',
        og_image_url: log.og_image_url ?? null,
        program_certificate: pc,
      });
      setLoading(false);
    }
    load();
    return () => { canceled = true; };
  }, [certLogId]);

  // Si og_image_url está poblado, usarlo directo. Si no, renderear el
  // diploma con el nombre y QR overlay on-the-fly con Canvas (misma
  // función que usa la vista previa privada del perfil). Sin esto, la
  // vista pública mostraría el template sin nombre embebido cuando
  // og_image_url = NULL (ej: cert recién emitido o reemitido).
  useEffect(() => {
    if (!data) return;
    if (data.og_image_url) {
      setRenderedImageUrl(data.og_image_url);
      return;
    }
    const pc = data.program_certificate;
    const prefix = data.diploma_type === 'A' ? 'diploma_a' : 'diploma_b';
    const templateUrl: string | null = pc?.[`${prefix}_image_url`] ?? null;
    if (!templateUrl) return;
    const layout = normalizeDiplomaLayout(pc?.[`${prefix}_layout`]);
    const entities: string = pc?.[`${prefix}_entities`] ?? '';
    const name: string = pc?.[`${prefix}_name`] ?? data.certificate_name;

    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const blob = await generateDiplomaImage({
          participantName: data.user_name,
          diplomaName: name,
          certificateId: data.id,
          issuedAt: data.generated_at,
          backgroundImageUrl: templateUrl,
          publicUrl: `https://lideria.org/c/${data.id}`,
          entities,
          layout,
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setRenderedImageUrl(objectUrl);
      } catch (e) {
        console.error('render diploma on verify failed', e);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-3xl space-y-4">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-64 w-full" />
        </div>
        <FloatingWhatsAppButton />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="text-xl font-bold">Certificado no encontrado</h1>
            <p className="text-muted-foreground text-sm">
              El identificador no corresponde a ningún certificado emitido.
            </p>
            <Link to="/" className="inline-block text-primary text-sm hover:underline">Ir al inicio</Link>
          </CardContent>
        </Card>
        <FloatingWhatsAppButton />
      </div>
    );
  }

  const pc = data.program_certificate;
  const diplomaPrefix = data.diploma_type === 'A' ? 'diploma_a' : 'diploma_b';
  const diplomaName: string = pc?.[`${diplomaPrefix}_name`] ?? data.certificate_name;
  const diplomaDescription: string = pc?.[`${diplomaPrefix}_description`] ?? '';
  const diplomaImage: string | null = pc?.[`${diplomaPrefix}_image_url`] ?? null;
  const diplomaSkills: any[] = pc?.[`${diplomaPrefix}_skills`] ?? [];
  const diplomaEntities: string = pc?.[`${diplomaPrefix}_entities`] ?? 'Líder IA Academy';
  const diplomaInfo: string = pc?.[`${diplomaPrefix}_additional_info`] ?? '';
  const additionalUrl: string | null = pc?.[`${diplomaPrefix}_additional_url`] ?? null;
  const requirements: string[] = [];
  if (pc?.[`${diplomaPrefix}_require_content`]) requirements.push(`Contenido obligatorio (${pc[`${diplomaPrefix}_min_content_percent`] ?? 100}%)`);
  if (pc?.[`${diplomaPrefix}_require_deliverables`]) requirements.push(`Entregables (${pc[`${diplomaPrefix}_min_deliverables_percent`] ?? 100}%)`);
  if (pc?.[`${diplomaPrefix}_require_project`]) requirements.push('Proyecto final entregado');
  if (pc?.[`${diplomaPrefix}_require_lab`]) requirements.push('Laboratorio de retos completado');

  const formattedDate = new Date(data.generated_at).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <SEO
        title={`Certificado verificado: ${diplomaName} — ${data.user_name}`.slice(0, 60)}
        description={`Verificación oficial del certificado "${diplomaName}" otorgado a ${data.user_name} por ${diplomaEntities} el ${formattedDate}.`}
        path={`/c/${certLogId}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: diplomaName,
          dateCreated: data.generated_at,
          creator: { "@type": "Organization", name: diplomaEntities },
          about: data.certificate_name,
          url: `https://lideria.org/c/${certLogId}`,
        }}
      />
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Hero card. Preferimos la imagen personalizada (og_image_url) que
            tiene el nombre del participante embebido; caemos al template si
            aún no se generó (se genera lazy al primer share). */}
        <Card className="overflow-hidden">
          {(renderedImageUrl || diplomaImage) && (
            <div className="w-full bg-muted">
              <img
                src={renderedImageUrl || diplomaImage!}
                alt="Diploma"
                className="w-full h-auto object-contain"
              />
            </div>
          )}
          <CardContent className="p-8 space-y-4">
            <div className="flex items-center gap-2 text-emerald-600">
              <ShieldCheck className="w-5 h-5" />
              <span className="text-sm font-medium">Certificado verificado</span>
            </div>
            <h1 className="text-3xl font-bold">{diplomaName}</h1>
            <p className="text-lg text-muted-foreground">
              Otorgado a <span className="font-semibold text-foreground">{data.user_name}</span>
            </p>
            {diplomaDescription && (
              <p className="text-sm text-muted-foreground leading-relaxed">{diplomaDescription}</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Fecha de emisión</p>
                  <p className="text-sm font-medium">{formattedDate}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Emitido por</p>
                  <p className="text-sm font-medium">{diplomaEntities}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">ID del certificado</p>
                  <p className="text-sm font-mono">{data.id.slice(0, 8).toUpperCase()}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Skills */}
        {diplomaSkills.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Habilidades acreditadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {diplomaSkills.map((s: any, i: number) => (
                  <Badge key={i} variant="secondary">{typeof s === 'string' ? s : s.name}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Requirements */}
        {requirements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Requisitos cumplidos</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {requirements.map((r, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    {r}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Additional info */}
        {(diplomaInfo || additionalUrl) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Información adicional</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {diplomaInfo && <p className="text-sm text-muted-foreground whitespace-pre-line">{diplomaInfo}</p>}
              {additionalUrl && (
                <a href={additionalUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                  {additionalUrl}
                </a>
              )}
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground pt-4">
          Verificación pública generada por Líder IA Academy
        </p>
      </div>
      <FloatingWhatsAppButton />
    </div>
  );
}
